import { get, isEmpty, toNumber } from 'lodash';
import { ILayout } from 'src/interfaces';

import {
  areAllBackupsValid,
  areAllScreensValid,
  filterOutInvalidDurationChanges,
  hasInvalidOrDuplicateDate,
  isScreenValid,
} from '../ScheduleComponentPage.helper';
import {
  ChangeItem,
  IPositionGridLines,
  TScreensGridSections,
} from '../ScheduleComponentPage.interface';

export const useValidateScreenStatus = (
  layout: ILayout,
  screensGridSections: TScreensGridSections,
  positionsGridLines: IPositionGridLines[],
  scheduleName: string,
  hasUnsavedChanges: boolean,
  hasLocalScheduleChanges: boolean,
  screensGridSectionChanges: ChangeItem[],
) => {
  const allComponentsHaveValidDate = Object.values(screensGridSections).every(
    (layer) =>
      Object.values(layer).every((sections) =>
        sections.every(
          (section) => !hasInvalidOrDuplicateDate(section.components || []),
        ),
      ),
  );

  const isValidScheduleName = ((name) => name.length > 0 && name.length <= 255)(
    scheduleName.trim(),
  );

  const enabledSave =
    areAllScreensValid(
      screensGridSections,
      positionsGridLines,
      layout?.config,
    ) &&
    allComponentsHaveValidDate &&
    isValidScheduleName &&
    areAllBackupsValid(screensGridSections) &&
    hasUnsavedChanges;

  const hadScheduledAllSections = (layerIndex: number, screenIndex: number) => {
    const screenSections = screensGridSections?.[layerIndex]?.[screenIndex];

    if (!Array.isArray(screenSections)) return false;

    const isScreenEmpty = screenSections.every((section) =>
      isEmpty(section.components),
    );

    if (isScreenEmpty) return false;

    const currentGridLine = positionsGridLines.find(
      (item) => item.position === layerIndex,
    )?.gridLines?.[toNumber(screenIndex)];

    const rawGridLine = layout?.config?.gridLines?.[screenIndex];

    return isScreenValid(screenSections, currentGridLine, rawGridLine);
  };

  const hasAnyScheduledScreenForPosition = (layerIndex: number): boolean => {
    const layerScreens = screensGridSections?.[layerIndex];
    if (!layerScreens) return false;

    return Object.values(layerScreens).some((screenSections) => {
      if (!Array.isArray(screenSections)) return false;

      // Check if atleast one section in  screen has non-empty components
      return screenSections.some(
        (section: any) =>
          Array.isArray(section.components) && !isEmpty(section.components),
      );
    });
  };

  const hasComponentsWithoutStartDate = (
    layerIndex: number,
    screenIndex: number,
  ): boolean => {
    const sections = screensGridSections[layerIndex][screenIndex];

    return sections.some((section) =>
      hasInvalidOrDuplicateDate(section?.components || []),
    );
  };

  const hasInvalidBackups = (
    layerIndex: number,
    screenIndex: number,
  ): boolean => {
    const sections = get(screensGridSections, [layerIndex, screenIndex]);

    return !areAllBackupsValid({
      [layerIndex]: {
        [screenIndex]: sections,
      },
    });
  };

  const isPreviewEnabled = () => {
    if (!hasLocalScheduleChanges) return false;

    return (
      !isEmpty(
        filterOutInvalidDurationChanges(
          screensGridSectionChanges,
          screensGridSections,
        ),
      ) && allComponentsHaveValidDate
    );
  };

  return {
    enabledSave,
    hadScheduledAllSections,
    hasComponentsWithoutStartDate,
    hasInvalidBackups,
    isPreviewEnabled,
    hasAnyScheduledScreenForPosition,
  };
};
